import {
  ArrowRight,
  BadgeCheck,
  BedDouble,
  BookOpen,
  CalendarClock,
  ChevronDown,
  CircleDollarSign,
  Download,
  FileCheck2,
  GraduationCap,
  Landmark,
  MapPin,
  MessageCircle,
  Phone,
  Plane,
  School,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { useState } from "react";
import { earlyCollegeSchools, majorPaths, type EarlyCollegeCountry } from "@/lib/earlyCollegeData";

export const EARLY_COLLEGE_HOME_STEPS = [
  {
    age: "16",
    title: "Ахлах сургууль + коллежийн хичээл",
    copy: "АНУ-ын ахлах сургууль болон коллежийн хичээлээ зэрэг төлөвлөж, кредитээ эхлүүлнэ.",
    icon: School,
  },
  {
    age: "18",
    title: "Аттестат + Associate зэрэг",
    copy: "Ахлах сургуулийн аттестат болон Associate буюу 2 жилийн коллежийн зэргээ авна.",
    icon: BadgeCheck,
  },
  {
    age: "20",
    title: "Бакалаврын зэргээ авна",
    copy: "Их, дээд сургуульд 3-р курсээс үргэлжлүүлэн сураад бакалаврын зэргээ авах боломжтой.",
    icon: GraduationCap,
  },
] as const;

export const EARLY_COLLEGE_ADVISORS = [
  { name: "Зөвлөх Баяр", phone: "8080-6633", href: "tel:+97680806633" },
  { name: "Зөвлөх Булгаа", phone: "9507-5552", href: "tel:+97695075552" },
  { name: "Зөвлөх Сайхнаа", phone: "9092-6633", href: "tel:+97690926633" },
] as const;

const BROCHURE_URL = "/manus-storage/early-college-16plus-brochure-advisor-contacts_9d24ebc6.pdf";

export const EARLY_COLLEGE_VISA_STEPS = [
  ["01", "Зөвлөгөө уулзалт", "Хүүхдийн нас, анги, дүн, хэлний түвшин, зорилго, төсвийг хамт үнэлнэ."],
  ["02", "Гэрээ байгуулалт", "Үйлчилгээ, ажиллах хугацаа, төлбөр болон хийх ажлуудаа ойлгомжтой тохирно."],
  ["03", "Сургууль сонголт", "Мэргэжил, байршил, нас, байр, жилийн зардалд тохирсон сургуулиудыг сонгоно."],
  ["04", "Материал бүрдүүлэлт", "Дүнгийн тодорхойлолт, паспорт, санхүү болон бусад шаардлагатай материалыг бэлтгэнэ."],
  ["05", "Материал илгээлт", "Сонгосон сургуульд элсэлтийн хүсэлт, материалыг алдаагүй илгээнэ."],
  ["06", "Элсэлт ба I-20", "Элсэлтийн хариу авсны дараа I-20 баримтыг авч, F-1 визийн бэлтгэлээ эхлүүлнэ."],
  ["07", "SEVIS, DS-160, ярилцлага", "SEVIS төлбөр, DS-160 анкет, визийн ярилцлагын бэлтгэл болон цаг авалтыг хийнэ."],
  ["08", "Визийн дараах бэлтгэл", "Визийн шийдвэрийн дараа сургуулийн төлбөрийн хуваарь, байр, нислэг, явахын өмнөх бэлтгэлээ батална."],
] as const;

export const EARLY_COLLEGE_HOME_FAQS = [
  {
    question: "Хэзээнээс бэлдэж эхлэх вэ?",
    answer: "Хүүхэд явахаас дор хаяж 6–12 сарын өмнө бэлтгэлээ эхлүүлэхийг зөвлөе. Энэ хугацаанд сургууль, мэргэжил, англи хэл, баримт бичиг, санхүү болон байрны сонголтоо тайван төлөвлөх боломжтой.",
  },
  {
    question: "Хэлний оноо, мэдлэггүй бол яах вэ?",
    answer: "Хэлний оноогүй байх нь боломж хаагдсан гэсэн үг биш. Манай байгууллага IELTS зэрэг шалгалтад бэлтгэх чиглэл өгч, шаардлагатай тохиолдолд англи хэлний бэлтгэл курсээр дамжин үндсэн хөтөлбөрт орох сонголтыг хамт төлөвлөнө.",
  },
  {
    question: "Хүүхэд очоод хаана амьдрах вэ?",
    answer: "16 настай сурагчдад сургуулийн зөвшөөрсөн айлд байрлах зэрэг хяналттай сонголт түгээмэл байдаг. 17–18 наснаас зарим сургуулийн оюутны байрны сонголт нээгдэж болно. 18-аас дээш насанд сургуулийн байр, айлд байрлах эсвэл баталгаатай түрээсийн сонголтыг сургууль бүрийн дүрмийн дагуу шийднэ.",
  },
  {
    question: "Сургалтын төлбөрийг хэзээ төлөх вэ?",
    answer: "Визийн хураамж, SEVIS зэрэг визийн зардал нь визийн бэлтгэлийн үеийн тусдаа төлбөр юм. Сургалтын төлбөрийн үлдэгдэл болон төлөх хуваарь нь визийн шийдвэрийн дараа, АНУ-д очих үеийн сургуулийн нөхцөлөөс хамаарна. I-20 эсвэл суудал баталгаажуулах урьдчилгаа шаардлагатай эсэхийг сонгосон сургууль бүрээр урьдчилан тодруулж, бүх төлбөрийг танд ил тод тайлбарлана.",
  },
] as const;

function SectionHeading({ eyebrow, title, copy }: { eyebrow: string; title: string; copy?: string }) {
  return (
    <div className="max-w-3xl">
      <p className="text-xs font-extrabold tracking-[0.18em] text-indigo-700">{eyebrow}</p>
      <h2 className="mt-3 text-3xl font-black tracking-tight text-[#111a42] sm:text-4xl">{title}</h2>
      {copy ? <p className="mt-4 text-base leading-7 text-slate-600">{copy}</p> : null}
    </div>
  );
}

export default function EarlyCollegeHomeIntroduction() {
  const [schoolCountry, setSchoolCountry] = useState<EarlyCollegeCountry>("usa");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const schools = earlyCollegeSchools[schoolCountry];

  return (
    <section id="early-college-home" className="bg-[#f6f8ff] py-14 sm:py-20">
      <div className="container space-y-16 sm:space-y-24">
        <div className="relative overflow-hidden rounded-[2rem] bg-[#10183d] px-6 py-9 text-white shadow-2xl shadow-indigo-950/20 sm:px-10 sm:py-14">
          <div className="absolute -right-20 -top-28 h-72 w-72 rounded-full bg-violet-500/35 blur-3xl" />
          <div className="absolute -bottom-32 left-1/3 h-72 w-72 rounded-full bg-sky-400/20 blur-3xl" />
          <div className="relative grid gap-10 lg:grid-cols-[1.08fr_.92fr] lg:items-center">
            <div>
              <div className="flex flex-wrap items-center gap-2 text-sm font-bold">
                <span className="rounded-full border border-white/25 bg-white/10 px-3 py-1.5">🇺🇸 АНУ</span>
                <span className="rounded-full border border-white/25 bg-white/10 px-3 py-1.5">🇨🇦 Канад</span>
              </div>
              <p className="mt-6 text-xs font-extrabold tracking-[0.18em] text-violet-200">АНУ–КАНАД 2+2 EARLY COLLEGE ХӨТӨЛБӨР</p>
              <h1 className="mt-3 text-4xl font-black tracking-[-0.045em] sm:text-5xl">Хүүхэддээ хийх хамгийн зөв хөрөнгө оруулалт</h1>
              <p className="mt-5 max-w-2xl text-base leading-7 text-white/75 sm:text-lg">
                16 наснаасаа АНУ-ын ахлах сургууль болон коллежийн хичээлээ зэрэг төлөвлөж, 18 насандаа аттестат ба Associate зэрэг авч, их сургуульд 3-р курсээс үргэлжлүүлэн сураад 20 насандаа бакалаврын зэрэг авах боломжтой.
              </p>
              <a href="#advisors" className="mt-7 inline-flex items-center justify-center gap-2 rounded-xl bg-white px-5 py-3.5 text-sm font-extrabold text-[#121a42] transition hover:bg-violet-100 active:scale-[0.98]">
                <MessageCircle className="h-4 w-4" /> Зөвлөхүүдийн дугаар харах
              </a>
            </div>

            <div className="rounded-3xl border border-white/15 bg-white/[0.08] p-5 backdrop-blur-sm sm:p-6">
              <p className="text-xs font-extrabold tracking-[0.16em] text-violet-200">16 → 18 → 20</p>
              <div className="mt-5 space-y-3">
                {EARLY_COLLEGE_HOME_STEPS.map((step) => {
                  const Icon = step.icon;
                  return (
                    <div key={step.age} className="flex gap-4 rounded-2xl border border-white/10 bg-[#0b1233]/60 p-4">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-400 to-indigo-500 text-lg font-black shadow-lg shadow-violet-950/20">{step.age}</div>
                      <div>
                        <div className="flex items-center gap-2 text-sm font-extrabold"><Icon className="h-4 w-4 text-sky-200" /> {step.title}</div>
                        <p className="mt-1 text-sm leading-5 text-white/65">{step.copy}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <p className="relative mt-7 border-t border-white/10 pt-4 text-xs leading-5 text-white/55">АНУ-ын 2+2 жишиг тайлбар. Хөтөлбөр, кредит шилжих болон бакалаврын хугацаа нь сургууль, мэргэжил, голч дүн, элсэлтийн шаардлагаас хамаарна.</p>
        </div>

        <div>
          <SectionHeading eyebrow="НЭГ ШИЙДВЭР. ГУРВАН ҮР ДҮН." title="Нэг алхмаар 3 боломж" copy="Хүүхэд тань ахлах сургуулийн аттестат, коллежийн зэрэг, бакалаврын зэрэг гэсэн дараалсан үр дүнг төлөвлөх боломжтой." />
          <div className="mt-8 grid gap-4 md:grid-cols-3">
            {[
              ["01", "Ахлах сургуулийн аттестат", "16–18 насанд ахлах сургуулийн хичээл, дипломын шаардлагаа хангана.", School],
              ["02", "Коллежийн зэрэг", "Associate буюу 2 жилийн коллежийн зэргээ авч, их сургуульд шилжих кредитээ цуглуулна.", Landmark],
              ["03", "Бакалаврын зэрэг", "Шилжих сургуулийн шаардлагаа хангаад бакалаврын хөтөлбөрөө дуусгана.", GraduationCap],
            ].map(([number, title, copy, Icon]) => {
              const CardIcon = Icon as typeof School;
              return (
                <div key={number as string} className="rounded-3xl border border-indigo-100 bg-white p-6 shadow-sm">
                  <div className="flex items-center justify-between"><span className="text-3xl font-black text-indigo-100">{number as string}</span><CardIcon className="h-6 w-6 text-indigo-600" /></div>
                  <h3 className="mt-8 text-lg font-black text-slate-900">{title as string}</h3>
                  <p className="mt-3 text-sm leading-6 text-slate-600">{copy as string}</p>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-end">
            <SectionHeading eyebrow="СУРГУУЛЬ БА ЖИЛИЙН ТӨЛБӨР" title="9 сар сураад, 3 сар амарна" copy="Доорх нь 1 хичээлийн жилийн жишиг төлбөр бөгөөд сургууль, хот, байр болон сонгосон хичээлээс шалтгаалан өөрчлөгдөнө." />
            <div className="inline-flex w-fit rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
              {(["usa", "canada"] as EarlyCollegeCountry[]).map((item) => (
                <button key={item} onClick={() => setSchoolCountry(item)} className={`rounded-lg px-4 py-2 text-sm font-bold transition ${schoolCountry === item ? "bg-[#151d47] text-white" : "text-slate-500 hover:text-slate-800"}`}>
                  {item === "usa" ? "🇺🇸 АНУ" : "🇨🇦 Канад"}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-8 grid gap-4 lg:grid-cols-3">
            {schools.map((school) => (
              <a key={school.name} href={school.source} target="_blank" rel="noreferrer" className="group rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-indigo-300 hover:shadow-lg">
                <div className="flex items-start justify-between gap-3"><div><h3 className="text-lg font-black text-slate-900">{school.name}</h3><p className="mt-1 flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" /> {school.location}</p></div><ArrowRight className="h-5 w-5 text-indigo-400 transition group-hover:translate-x-1" /></div>
                <div className="mt-6 rounded-2xl bg-indigo-50 p-4"><p className="text-xs font-bold tracking-[0.12em] text-indigo-700">СУРГАЛТЫН ТӨЛБӨР</p><p className="mt-1 text-2xl font-black text-[#151d47]">{school.tuition}</p><p className="mt-1 text-xs text-slate-500">{school.tuitionNote}</p></div>
                <div className="mt-4 flex items-start gap-2"><CircleDollarSign className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /><p className="text-sm font-bold text-slate-800">Жилийн нийт жишиг: {school.annualTotal}</p></div>
                <p className="mt-2 text-xs leading-5 text-slate-500">{school.totalNote}</p>
              </a>
            ))}
          </div>
        </div>

        <div>
          <SectionHeading eyebrow="МЭРГЭЖЛЭЭ ЭРТ ТӨЛӨВЛӨ" title="Суралцах мэргэжлүүд" copy="Суурь хичээлээ зорилготойгоор сонговол коллежийн кредит болон дараагийн их сургуулийн мэргэжлээ уялдуулахад илүү хялбар." />
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {majorPaths.map((major) => (
              <div key={major.title} className="rounded-2xl border border-slate-200 bg-white p-5"><span className="text-2xl">{major.icon}</span><h3 className="mt-4 font-black text-slate-900">{major.title}</h3><p className="mt-2 text-sm leading-6 text-slate-600">{major.text}</p></div>
            ))}
          </div>
        </div>

        <div className="grid gap-8 xl:grid-cols-[1.05fr_.95fr]">
          <div>
            <SectionHeading eyebrow="ХАМГИЙН ИХ АСУУДАГ АСУУЛТУУД" title="Эцэг эхэд хэрэгтэй хариултууд" />
            <div className="mt-7 overflow-hidden rounded-3xl border border-slate-200 bg-white">
              {EARLY_COLLEGE_HOME_FAQS.map((faq, index) => {
                const open = openFaq === index;
                return (
                  <div key={faq.question} className="border-b border-slate-100 last:border-b-0">
                    <button onClick={() => setOpenFaq(open ? null : index)} className="flex w-full items-center justify-between gap-5 px-5 py-5 text-left sm:px-6"><span className="font-extrabold text-slate-900">{faq.question}</span><ChevronDown className={`h-5 w-5 shrink-0 text-indigo-600 transition ${open ? "rotate-180" : ""}`} /></button>
                    {open ? <p className="px-5 pb-5 text-sm leading-6 text-slate-600 sm:px-6 sm:pb-6">{faq.answer}</p> : null}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-[2rem] bg-gradient-to-br from-[#132052] to-[#26145c] p-6 text-white shadow-xl shadow-indigo-950/20 sm:p-8">
            <p className="text-xs font-extrabold tracking-[0.18em] text-violet-200">ЗАРДЛЫН ИЛ ТОД ТӨЛӨВЛӨЛТ</p>
            <h2 className="mt-3 text-3xl font-black">Зардлыг хоёр хэсэгт ойлгоорой</h2>
            <div className="mt-6 space-y-4">
              <div className="rounded-2xl border border-white/15 bg-white/10 p-5"><p className="flex items-center gap-2 text-sm font-black"><FileCheck2 className="h-4 w-4 text-violet-200" /> Виз мэдүүлэх хүртэл</p><ul className="mt-3 space-y-2 text-sm leading-6 text-white/75"><li>• АНУ-ын F-1 визийн хураамж: <strong className="text-white">USD 185</strong></li><li>• I-901 SEVIS төлбөр: <strong className="text-white">USD 350</strong></li><li>• Орчуулга, бичиг баримт, шаардлагатай нэмэлт зардал</li><li>• Зуучлалын хөлс: хүүхдийн нөхцөл, сургууль, материалын ажлаас шалтгаалан тусад нь тооцно</li></ul></div>
              <div className="rounded-2xl border border-white/15 bg-[#090e2b]/35 p-5"><p className="flex items-center gap-2 text-sm font-black"><BedDouble className="h-4 w-4 text-sky-200" /> Визийн шийдвэрийн дараа</p><ul className="mt-3 space-y-2 text-sm leading-6 text-white/75"><li>• Сургалтын төлбөрийн үлдэгдэл: сургуулийн баталсан төлбөрийн хуваарийн дагуу</li><li>• 1 жилийн байр, хоол, даатгал, ном, тээвэр, хувийн хэрэглээ</li><li>• Жишиг нийт зардал: сонгосон сургуулийн тооцоогоор <strong className="text-white">ойролцоогоор USD 23,475–28,500</strong> хүрч болно</li></ul></div>
            </div>
            <p className="mt-5 rounded-xl border border-amber-200/20 bg-amber-200/10 p-3 text-xs leading-5 text-amber-50">Сургалтын төлбөр, I-20 болон суудал баталгаажуулах урьдчилгааны журам сургууль бүрд ялгаатай. Төлбөр хийхээс өмнө сонгосон сургуулийн нөхцөлийг таны нэр дээр бичгээр нягталж тайлбарлана.</p>
          </div>
        </div>

        <div>
          <SectionHeading eyebrow="ВИЗ МЭДҮҮЛЭХ ДЭС ДАРААЛАЛ" title="Зөвлөгөөнөөс I-20 хүртэл, дараа нь визийн бэлтгэл" copy="Бүх ажлыг нэг дор хийдэггүй. Доорх дарааллаар хамт төлөвлөснөөр гэр бүл бүр дараагийн алхмаа ойлгомжтой харна." />
          <div className="mt-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {EARLY_COLLEGE_VISA_STEPS.map(([number, title, copy]) => (
              <div key={number} className="rounded-2xl border border-slate-200 bg-white p-5"><div className="flex items-center justify-between"><span className="text-2xl font-black text-indigo-100">{number}</span><Plane className="h-5 w-5 text-indigo-600" /></div><h3 className="mt-6 font-black text-slate-900">{title}</h3><p className="mt-2 text-sm leading-6 text-slate-600">{copy}</p></div>
            ))}
          </div>
        </div>

        <div className="rounded-[2rem] border border-indigo-100 bg-white p-6 shadow-lg shadow-indigo-950/5 sm:p-10">
          <div className="grid gap-7 lg:grid-cols-[1fr_auto] lg:items-center">
            <div><p className="text-xs font-extrabold tracking-[0.18em] text-indigo-700">ДАРААГИЙН АЛХАМ</p><h2 className="mt-3 text-3xl font-black tracking-tight text-[#111a42]">Хүүхдийнхээ төлөвлөгөөг зөвлөгөөнөөс эхлүүлээрэй</h2><p className="mt-4 max-w-2xl text-base leading-7 text-slate-600">Нас, одоогийн анги, дүн, англи хэл, сонирхож буй мэргэжил болон гэр бүлийн төсөвт тохирсон сургуулийн сонголт, бэлтгэлийн хугацаа, зардлын урьдчилсан төлөвлөгөөг гаргаж өгнө.</p></div>
            <div id="advisors" className="space-y-3 rounded-2xl bg-indigo-50 p-5 text-sm">{EARLY_COLLEGE_ADVISORS.map((advisor) => <a key={advisor.phone} href={advisor.href} className="flex items-center justify-between gap-3 rounded-xl border border-indigo-100 bg-white px-4 py-3 font-extrabold text-[#111a42] transition hover:border-indigo-300 hover:bg-indigo-50"><span className="text-sm">{advisor.name}</span><span className="flex items-center gap-2 text-base"><Phone className="h-4 w-4 text-indigo-600" /> {advisor.phone}</span></a>)}<div className="flex items-start gap-3 pt-2 font-semibold leading-5 text-slate-700"><MapPin className="mt-0.5 h-5 w-5 shrink-0 text-indigo-600" /> СУИС-ийн урд, Gem Castle төв, 9 давхар, 907 тоот</div><div className="flex items-center gap-3 font-semibold text-slate-700"><CalendarClock className="h-5 w-5 text-indigo-600" /> Даваа–Баасан 09:00–18:00 · Ням 13:00–17:00</div><a href={BROCHURE_URL} download="United-Dream-Early-College-16plus.pdf" className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#151d47] px-4 py-3 font-extrabold text-white transition hover:bg-indigo-700"><Download className="h-4 w-4" /> Танилцуулга PDF татах</a></div>
          </div>
        </div>
      </div>
    </section>
  );
}
